/**
 * Mobile-only bottom banner: "Create Chat PWA" – prompts users to install the chat app on their phone.
 * Shown when on mobile, not already installed (standalone), and not dismissed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal } from 'antd';
import { MobileOutlined, CloseOutlined } from '@ant-design/icons';
import './MobileChatPwaBanner.css';

const STORAGE_KEY_DISMISSED = 'pwa-chat-banner-dismissed';
const DISMISS_DAYS = 7;

const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    nav.standalone === true ||
    document.referrer.includes('android-app://')
  );
};

const wasDismissedRecently = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DISMISSED);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    if (Number.isNaN(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
};

const setDismissed = () => {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY_DISMISSED, String(until));
  } catch (_) {}
};

type DeferredPrompt = { prompt: () => void; userChoice: Promise<{ outcome: string }> };

const isAndroid = (): boolean =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const isIOS = (): boolean =>
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

export const MobileChatPwaBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<DeferredPrompt | null>(null);

  const updateVisibility = useCallback(() => {
    if (!isMobile() || isStandalone() || wasDismissedRecently()) {
      setVisible(false);
      return;
    }
    setIsMobileDevice(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const evt = e as unknown as DeferredPrompt;
      setDeferredPrompt(evt);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('resize', updateVisibility);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [updateVisibility]);

  // On Android: show banner once we have the prompt (one-tap ready) or after 2.5s fallback
  useEffect(() => {
    if (!isMobile() || isStandalone() || wasDismissedRecently()) return;
    setIsMobileDevice(true);
    if (isAndroid()) {
      if (deferredPrompt) {
        setVisible(true);
        return;
      }
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }
    setVisible(true);
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setVisible(false);
        setDeferredPrompt(null);
      } catch (_) {
        setInstructionsVisible(true);
      } finally {
        setInstalling(false);
      }
      return;
    }
    setInstructionsVisible(true);
  };

  const handleDismiss = () => {
    setDismissed();
    setVisible(false);
  };

  if (!visible || !isMobileDevice) return null;

  return (
    <>
      <div className="mobile-chat-pwa-banner">
        <div className="mobile-chat-pwa-banner-inner">
          <MobileOutlined className="mobile-chat-pwa-banner-icon" />
          <span className="mobile-chat-pwa-banner-text">Use Chat in an app</span>
          <Button
            type="primary"
            size="small"
            className="mobile-chat-pwa-banner-btn"
            onClick={handleInstall}
            loading={installing}
            disabled={installing}
          >
            {installing ? 'Opening install…' : 'Create Chat PWA'}
          </Button>
          <button type="button" className="mobile-chat-pwa-banner-close" onClick={handleDismiss} aria-label="Dismiss">
            <CloseOutlined />
          </button>
        </div>
      </div>

      <Modal
        title="Add Chat to Home Screen"
        open={instructionsVisible}
        onCancel={() => setInstructionsVisible(false)}
        footer={
          <Button type="primary" onClick={() => setInstructionsVisible(false)}>
            Got it
          </Button>
        }
      >
        {isAndroid() ? (
          <p>
            <strong>Android:</strong> Refresh this page, then tap &quot;Create Chat PWA&quot; again — the install dialog should appear. Or open Chrome’s menu (⋮) and choose &quot;Add to Home screen&quot; or &quot;Install app&quot;.
          </p>
        ) : isIOS() ? (
          <p>
            <strong>iPhone / iPad:</strong> Tap the Share button (□↑) at the bottom of Safari, then &quot;Add to Home Screen&quot;. The new icon will open Chat like an app.
          </p>
        ) : (
          <>
            <p><strong>Android:</strong> Refresh the page then tap &quot;Create Chat PWA&quot; again, or use Chrome menu (⋮) → Add to Home screen.</p>
            <p><strong>iPhone / iPad:</strong> In Safari, use Share (□↑) → Add to Home Screen.</p>
          </>
        )}
      </Modal>
    </>
  );
};
